import React, { useEffect, useState } from "react";
import { tvFloorDay } from "../../lib/api";

const G = "#10F5A0", R = "#FF3B6B", C = "#00F0FF", A = "#F5C451";
const usd = (v) => `${v < 0 ? "-" : v > 0 ? "+" : ""}$${Math.abs(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const PER = 3, PAGE_MS = 12000;

const Stat = ({ k, v, color = C, sub, testid }) => (
  <div className="nx-hud rounded-2xl px-5 py-3" data-testid={testid}>
    <p className="nx-mono text-[11px] tracking-[0.3em] text-cyan-200/50">{k}</p>
    <p className="nx-orbitron font-black text-[clamp(1.3rem,2vw,2.3rem)] leading-tight truncate" style={{ color, textShadow: `0 0 16px ${color}55` }}>{v}</p>
    {sub && <p className="nx-mono text-xs text-cyan-100/50 truncate">{sub}</p>}
  </div>
);

const Chart = ({ c }) => {
  const s = c.chart || [];
  if (s.length < 2) return <div className="h-full grid place-items-center nx-mono text-sm text-cyan-200/40">no intraday chart yet</div>;
  const W = 600, H = 200, ts = s.map((p) => p[0]), px = s.map((p) => p[1]);
  const lo = Math.min(...px, Number(c.entry) || Infinity), hi = Math.max(...px, Number(c.entry) || -Infinity), span = hi - lo || 1;
  const t0 = ts[0], t1 = ts[ts.length - 1], tsp = t1 - t0 || 1;
  const x = (t) => ((Math.min(Math.max(t, t0), t1) - t0) / tsp) * W, y = (v) => H - 8 - ((v - lo) / span) * (H - 16);
  const col = c.pnl_usd >= 0 ? G : R;
  const path = s.map((p, i) => `${i ? "L" : "M"}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(" ");
  const mk = (t, v, label, fill) => t && v != null && (
    <g><circle cx={x(t)} cy={y(Number(v))} r="7" fill={fill} stroke="#030712" strokeWidth="2" />
      <text x={Math.min(x(t) + 10, W - 60)} y={y(Number(v)) - 10} fill={fill} fontSize="18" fontFamily="monospace" fontWeight="700">{label}</text></g>);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="none" data-testid={`tv-floorday-chart-${c.id}`}>
      <line x1="0" x2={W} y1={y(Number(c.entry))} y2={y(Number(c.entry))} stroke={`${A}55`} strokeDasharray="6 6" />
      <path d={`${path} L${W},${H} L0,${H} Z`} fill={`${col}18`} />
      <path d={path} fill="none" stroke={col} strokeWidth="3" style={{ filter: `drop-shadow(0 0 4px ${col})` }} />
      {mk(c.opened_ts, c.entry, "IN", A)}
      {c.closed_ts && mk(c.closed_ts, c.exit, "OUT", col)}
    </svg>
  );
};

const TradeCard = ({ c }) => {
  const col = c.pnl_usd >= 0 ? G : R;
  return (
    <div className="nx-hud rounded-3xl p-4 flex flex-col gap-2 min-h-0 overflow-hidden" style={{ borderColor: `${col}55` }} data-testid={`tv-floorday-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="nx-orbitron font-black text-2xl text-cyan-50 truncate">{c.symbol}</p>
          <p className="nx-mono text-sm text-cyan-200/60">{String(c.side || "").toUpperCase()} · {c.qty} · {c.venue} · {c.status === "open" ? "OPEN" : "CLOSED"}</p>
        </div>
        <div className="text-right shrink-0" data-testid={`tv-floorday-pnl-${c.id}`}>
          <p className="nx-orbitron font-black text-3xl" style={{ color: col, textShadow: `0 0 14px ${col}66` }}>{usd(c.pnl_usd)}</p>
          <p className="nx-mono text-base font-bold" style={{ color: col }}>{c.pnl_pct >= 0 ? "+" : ""}{Number(c.pnl_pct || 0).toFixed(2)}%</p>
        </div>
      </div>
      <p className="nx-mono text-sm text-cyan-100/80">in {c.entry ?? "—"} → {c.status === "open" ? "now" : "out"} {c.exit ?? "—"}</p>
      <div className="h-[24%] min-h-[110px] rounded-xl bg-black/30 border border-white/5 p-1"><Chart c={c} /></div>
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-1 text-[clamp(0.85rem,0.95vw,1.1rem)] leading-snug">
        <p className="nx-mono text-[11px] tracking-[0.3em] text-cyan-300/70">TECHNICAL</p>
        {c.technical.map((t, i) => <p key={i} className="text-cyan-50/85">• {t}</p>)}
        <p className="nx-mono text-[11px] tracking-[0.3em] text-amber-300/70 mt-1">FUNDAMENTAL</p>
        {c.fundamental.map((t, i) => <p key={i} className="text-cyan-50/85">• {t}</p>)}
        <p className="nx-mono text-[11px] tracking-[0.3em] text-emerald-300/70 mt-1">WHY WE TOOK IT</p>
        <p className="text-cyan-50 font-semibold" data-testid={`tv-floorday-logic-${c.id}`}>{c.logic}</p>
      </div>
    </div>
  );
};

export const TvFloorDay = () => {
  const [d, setD] = useState(null);
  const [page, setPage] = useState(0);
  useEffect(() => {
    const load = () => tvFloorDay().then(setD).catch(() => {});
    load(); const t = setInterval(load, 60000); return () => clearInterval(t);
  }, []);
  const cards = d?.cards || [];
  const pages = Math.max(1, Math.ceil(cards.length / PER));
  useEffect(() => { const t = setInterval(() => setPage((p) => (p + 1) % pages), PAGE_MS); return () => clearInterval(t); }, [pages]);
  const show = cards.slice((page % pages) * PER, (page % pages) * PER + PER);
  const tc = (d?.total_pnl_usd || 0) >= 0 ? G : R;
  return (
    <div className="h-full flex flex-col gap-4" data-testid="tv-scene-floor-day">
      <div className="grid grid-cols-6 gap-4 shrink-0">
        <Stat k="TODAY P/L" v={usd(d?.total_pnl_usd || 0)} color={tc} sub={`${(d?.total_pnl_pct || 0) >= 0 ? "+" : ""}${d?.total_pnl_pct || 0}% of capital used`} testid="tv-floorday-total" />
        <Stat k="TRADES" v={d?.trades || 0} testid="tv-floorday-trades" />
        <Stat k="WIN RATE" v={`${d?.win_rate || 0}%`} color={A} sub="share of trades in profit" testid="tv-floorday-winrate" />
        <Stat k="BEST" v={d?.best || "—"} color={G} sub={d?.best ? usd(d.best_usd) : ""} testid="tv-floorday-best" />
        <Stat k="WORST" v={d?.worst || "—"} color={R} sub={d?.worst ? usd(d.worst_usd) : ""} testid="tv-floorday-worst" />
        <Stat k="PAGE" v={`${(page % pages) + 1}/${pages}`} sub={`US/Eastern day ${d?.day || ""}`} testid="tv-floorday-page" />
      </div>
      {cards.length === 0
        ? <div className="flex-1 grid place-items-center nx-orbitron text-4xl text-cyan-200/50" data-testid="tv-floorday-empty">No trades yet today</div>
        : <div key={page} className="flex-1 min-h-0 grid grid-cols-3 gap-4 nx-fade-up">{show.map((c) => <TradeCard key={c.id} c={c} />)}</div>}
    </div>
  );
};
